import dayjs from 'dayjs';
import { snakeCase } from 'lodash';
import {
  defaultEventCategorySet,
  RoutineActivity,
  RoutineActivityRecurring,
} from '@demind-inc/core';

import { ChatSuggestedEvent } from '../types';
import { RoutineAdjustment } from '../api/science/openapi';

export const TEMPORAL_ROUTINE_PREFIX = 'temporal-';

const WEEKDAYS: (keyof RoutineActivityRecurring)[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

/** Ports lifestack_backend's transformAdjustmentCTA recurring normalization
 * client-side — same per-weekday shape. */
const normalizeRecurring = (
  recurring: RoutineAdjustment['recurring']
): RoutineActivityRecurring | undefined => {
  if (!recurring) {
    return undefined;
  }
  return WEEKDAYS.reduce((acc, day) => {
    acc[day] = { enabled: Boolean((recurring as Record<string, { enabled?: boolean }>)[day]?.enabled ?? false) };
    return acc;
  }, {} as RoutineActivityRecurring);
};

/** Same category lookup as lifestack_backend's transformAdjustmentCTA:
 * display name, or the snake_case of that name (`light_task` → Light Task). */
const resolveCategoryId = (category: string | null | undefined): string | undefined => {
  if (!category) {
    return undefined;
  }
  const normalized = snakeCase(category);
  return defaultEventCategorySet.find(
    (c) =>
      c.id === category ||
      c.name.toLowerCase() === category.toLowerCase() ||
      snakeCase(c.name) === normalized
  )?.id;
};

/** Ports lifestack_backend's isSameBeforeAfterRoutine — skip rendering a
 * "modify" suggestion when nothing actually changed vs. the user's current
 * routine. */
const isSameBeforeAfterRoutine = (
  before: RoutineActivity | undefined,
  after: {
    name: string;
    startTime?: string;
    endTime?: string;
    durationMin?: number;
    categoryId?: string;
  }
): boolean => {
  if (!before) {
    return false;
  }
  return (
    before.name === after.name &&
    (before.startTime ?? undefined) === after.startTime &&
    (before.endTime ?? undefined) === after.endTime &&
    before.durationMin === after.durationMin &&
    before.category?.id === after.categoryId
  );
};

const toISOTime = (time: string, date: string): string =>
  dayjs(time).isValid() ? dayjs(time).toISOString() : dayjs(`${date} ${time}`).toISOString();

/**
 * Maps a v4 result_json's raw `adjustments.routine_adjustments`
 * (RoutineAdjustment[]) into ChatSuggestedEvent[], rendered by
 * AssistantChatSuggestedRoutineBlock (action: 'setRoutine' | 'deleteRoutine').
 * `currentRoutineActivities` should come from useRoutineCache().findInCache().
 */
export const transformResultJsonRoutinesToSuggestedEvents = (
  routines: RoutineAdjustment[],
  date: string,
  currentRoutineActivities: RoutineActivity[]
): ChatSuggestedEvent[] => {
  const findCurrent = (routineActivityId: string | null | undefined) =>
    routineActivityId
      ? currentRoutineActivities.find((r) => r.routineActivityId === routineActivityId)
      : undefined;

  const blocks: ChatSuggestedEvent[] = [];

  routines.forEach((routine) => {
    if (!routine.name) {
      return;
    }

    if (routine.action === 'remove') {
      if (!routine.routineActivityId) {
        return;
      }
      const before = findCurrent(routine.routineActivityId);
      const startTime = before?.startTime ?? routine.startTime;
      const endTime = before?.endTime ?? routine.endTime;
      if (!startTime || !endTime) {
        return;
      }
      blocks.push({
        eventId: routine.routineActivityId,
        name: before?.name ?? routine.name,
        startTime: toISOTime(startTime, date),
        endTime: toISOTime(endTime, date),
        durationMin: before?.durationMin ?? routine.durationMin ?? undefined,
        recurring: before?.recurring as RoutineActivityRecurring | undefined,
        autoScheduleFrom: { from: 'routine', id: routine.routineActivityId, predicted: true },
        isNew: false,
        action: 'deleteRoutine',
      });
      return;
    }

    // 'add' | 'modify'
    const startTime = routine.startTime;
    const endTime = routine.endTime;
    if (!startTime || !endTime) {
      return;
    }

    const categoryId = resolveCategoryId(routine.category);
    const recurring = normalizeRecurring(routine.recurring);
    const eventId =
      routine.routineActivityId || `${TEMPORAL_ROUTINE_PREFIX}routine-${routine.name}`;

    if (routine.action === 'modify' && routine.routineActivityId) {
      const before = findCurrent(routine.routineActivityId);
      if (
        isSameBeforeAfterRoutine(before, {
          name: routine.name,
          startTime,
          endTime,
          durationMin: routine.durationMin ?? undefined,
          categoryId,
        })
      ) {
        return;
      }
    }

    const before = routine.routineActivityId ? findCurrent(routine.routineActivityId) : undefined;

    blocks.push({
      eventId,
      name: routine.name,
      startTime: toISOTime(startTime, date),
      endTime: toISOTime(endTime, date),
      durationMin: routine.durationMin ?? undefined,
      categoryId,
      recurring,
      ...(before && {
        before: {
          id: before.routineActivityId,
          startTime: before.startTime ?? undefined,
          endTime: before.endTime ?? undefined,
          name: before.name,
          categoryId: before.category?.id,
        },
      }),
      autoScheduleFrom: { from: 'routine', id: eventId, predicted: true },
      isNew: !before,
      action: 'setRoutine',
    });
  });

  return blocks;
};
